import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import "./AboutMe.css";
import { Link } from "react-scroll";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';


const AboutMe = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, x: 80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="AboutMe" className="about-section" ref={sectionRef} aria-labelledby="about-heading">
      <div className="about-container">
        <motion.div
          className="about-visual"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <DotLottieReact
          src="../img/about.lottie"
          loop
          autoplay
          width={200}
          height={200}
        />
          
        </motion.div>

        <motion.div
          className="about-content"
          variants={contentVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.p 
            className="section-subtitle"
            variants={textVariants}
          >
            Professional Profile
          </motion.p>
          
          <motion.h1 
            id="about-heading" 
            className="section-heading"
            variants={textVariants}
          >
            Crafting Digital Excellence
          </motion.h1>

          <motion.div 
            className="description-container"
            variants={containerVariants}
          >
            <motion.p 
              className="section-description"
              variants={textVariants}
            >
              I'm <strong className="highlight-text">Griach Tahar</strong>, a passionate Full Stack Developer based in Larache, Morocco. 
              With a solid foundation in modern web technologies, I specialize in creating 
              <strong> responsive, performant, and user-centric digital experiences</strong>.
            </motion.p>

            <motion.p 
              className="section-description"
              variants={textVariants}
            >
              My expertise spans across the entire development stack, from crafting engaging 
              front-end interfaces with <strong>React</strong> to building robust back-end systems 
              with <strong>Laravel & Express.js</strong>. I also develop desktop applications 
              using <strong>Python Tkinter</strong> for comprehensive software solutions.
            </motion.p>

            <motion.div 
              className="qualifications"
              variants={textVariants}
            >
              <div className="qualification-item">
                <div className="qualification-badge">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span>Specialized Technician Diploma - Full Stack Development</span>
              </div>
              
              <div className="qualification-item">
                <div className="qualification-badge">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span>Cisco PCPA Certification</span>
              </div>
            </motion.div>

            <motion.div 
              className="cta-container"
              variants={textVariants}
            >
              <motion.a
                className="cta-button"
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 10px 30px rgba(0, 191, 166, 0.3)"
                }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <span>
                   <Link
          to="Contact"
          spy={true}
          smooth={true}
          offset={-70}
          duration={500}

        >
          Let's Work Together
        </Link>
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M7 17L17 7M17 7H8M17 7V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </motion.a>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutMe;